"use client"

import { startTransition, useEffect, useMemo, useRef, useState } from "react"
import { List } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { useTableOfContents, type TOCItem } from "@/hooks/use-table-of-contents"

interface StickySidebarTOCProps {
  content: string
  className?: string
}

interface TopLevelHeading extends TOCItem {
  childIds: string[]
}

export function StickySidebarTOC({ content, className }: StickySidebarTOCProps) {
  const { tocItems, shouldShowTOC } = useTableOfContents({
    content,
    showReadingTimes: false,
    minHeadings: 2,
  })
  const [activeId, setActiveId] = useState("")
  const activeIdRef = useRef("")

  // Desktop readers need a quiet map of the article, not a second copy of
  // every subheading. Keep H3s in the scroll spy so their parent H2 remains
  // highlighted while the reader moves through that section.
  const topLevelHeadings = useMemo(() => {
    const headings: TopLevelHeading[] = []
    tocItems.forEach((item) => {
      if (item.level === 2) headings.push({ ...item, childIds: [] })
      else if (headings.length > 0) headings[headings.length - 1].childIds.push(item.id)
    })
    return headings
  }, [tocItems])

  useEffect(() => {
    if (tocItems.length === 0) return
    let frame: number | null = null

    const updateActiveHeading = () => {
      frame = null
      const threshold = 120
      let nextId = tocItems[0]?.id ?? ""
      for (const item of tocItems) {
        const element = document.getElementById(item.id)
        if (!element) continue
        if (element.getBoundingClientRect().top <= threshold) nextId = item.id
        else break
      }
      if (nextId && nextId !== activeIdRef.current) {
        activeIdRef.current = nextId
        startTransition(() => setActiveId(nextId))
      }
    }

    const scheduleUpdate = () => {
      if (frame === null) frame = window.requestAnimationFrame(updateActiveHeading)
    }
    scheduleUpdate()
    window.addEventListener("scroll", scheduleUpdate, { passive: true })
    window.addEventListener("resize", scheduleUpdate, { passive: true })
    return () => {
      window.removeEventListener("scroll", scheduleUpdate)
      window.removeEventListener("resize", scheduleUpdate)
      if (frame !== null) window.cancelAnimationFrame(frame)
    }
  }, [tocItems])

  if (!shouldShowTOC || topLevelHeadings.length === 0) return null

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id)
    if (!element) return
    const top = element.getBoundingClientRect().top + window.scrollY - 100
    window.scrollTo({ top, behavior: "smooth" })
  }

  return (
    <div className={className}>
      <Card className="ds-glass flex max-h-[calc(100vh-7rem-var(--wg-bottom-dock-height,0px))] w-full flex-col rounded-[20px]">
        <CardHeader className="flex-shrink-0 border-b border-white/55 px-4 pb-2.5 pt-3">
          <CardTitle className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-green-700">
            <List className="h-3.5 w-3.5 text-green-600" aria-hidden="true" />
            On this page
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto p-2.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <nav aria-label="On this page" className="space-y-0.5">
            {topLevelHeadings.map((item) => {
              const isActive = activeId === item.id || item.childIds.includes(activeId)
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => scrollToSection(item.id)}
                  aria-current={isActive ? "location" : undefined}
                  className={cn(
                    "relative w-full rounded-xl py-2 pl-4 pr-3 text-left text-[13px] leading-snug transition-colors",
                    isActive
                      ? "bg-white/75 font-semibold text-green-800"
                      : "text-gray-700 hover:bg-white/55 hover:text-gray-900",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "absolute bottom-2 left-1.5 top-2 w-[3px] rounded-full",
                      isActive ? "bg-green-600" : "bg-transparent",
                    )}
                  />
                  {stripNumberPrefix(item.text)}
                </button>
              )
            })}
          </nav>
        </CardContent>
      </Card>
    </div>
  )
}

function stripNumberPrefix(text: string) {
  return text.replace(/^(\d+\.)+\s*/, "").trim()
}
